/**
 * Trivial greedy CPU for block dominoes.
 *
 * Heuristic: dump heavy tiles first. Among legal plays, pick the one with
 * the highest pip total — minimizes loss if the game blocks. If both ends
 * accept the chosen tile, prefer the side whose resulting outer pip is
 * less common in the rest of the hand (so we keep flexibility), with a
 * stable tie-break of 'right'.
 */

import type { Domino } from './domino.js';
import { otherHalf, pipTotal } from './domino.js';
import type { DominoGameState, Player, Side } from './game.js';
import { legalPlaysFor } from './game.js';
import { chainLeftEnd, chainRightEnd } from './chain.js';

export type CpuMove =
  | { kind: 'play'; dominoId: string; side: Side }
  | { kind: 'pass' };

function pipCountInHand(hand: readonly Domino[], pip: number, exclude: Domino): number {
  let n = 0;
  for (const d of hand) {
    if (d.id === exclude.id) continue;
    if (d.low === pip) n++;
    if (d.high === pip) n++;
  }
  return n;
}

export function pickCpuMove(state: DominoGameState, player: Player): CpuMove {
  const plays = legalPlaysFor(state, player);
  if (plays.length === 0) return { kind: 'pass' };

  let bestTile = plays[0]!.domino;
  let bestSides = plays[0]!.sides;
  let bestScore = pipTotal(bestTile);
  for (let i = 1; i < plays.length; i++) {
    const p = plays[i]!;
    const score = pipTotal(p.domino);
    if (score > bestScore) {
      bestScore = score;
      bestTile = p.domino;
      bestSides = p.sides;
    }
  }

  let side: Side = bestSides[0]!;
  if (bestSides.length > 1) {
    const left = chainLeftEnd(state.chain);
    const right = chainRightEnd(state.chain);
    const handAfter = state.hands[player];
    const leftOuter = left === null ? bestTile.low : otherHalf(bestTile, left);
    const rightOuter = right === null ? bestTile.high : otherHalf(bestTile, right);
    const leftFlex = pipCountInHand(handAfter, leftOuter, bestTile);
    const rightFlex = pipCountInHand(handAfter, rightOuter, bestTile);
    side = rightFlex >= leftFlex ? 'right' : 'left';
  }

  return { kind: 'play', dominoId: bestTile.id, side };
}
